// dwaar/src/components/PaymentGate.jsx
// Repo: terralekha-core | Folder: dwaar/src/components/
// PaymentGate — wraps any child element with Razorpay payment access logic.
//
// Props:
//   passportId  — UUID of the passport to check
//   accessToken — Supabase Bearer token
//   children    — rendered directly when access is allowed
//
// Behaviour:
//   1. On mount: GET /api/shulka/status/{passportId}
//   2. allowed → render children as-is
//   3. not allowed → fetch pricing, show payment panel with two options
//   4. Per-passport: POST /orders → openRazorpayCheckout → POST /verify → re-fetch
//   5. Subscription: POST /subscription/orders → openRazorpayCheckout →
//                    POST /subscription/verify → re-fetch

import React, { useCallback, useEffect, useState } from "react";
import { openRazorpayCheckout } from "../lib/razorpay.js";
import { PUBLIC_ERROR } from "../lib/brand.js";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

function paise(p) { return `₹${Math.round(p / 100)}`; }

export default function PaymentGate({ passportId, accessToken, children }) {
  const [status, setStatus] = useState(null);   // null=loading, {allowed,reason}
  const [plan,   setPlan]   = useState(null);
  const [paying, setPaying] = useState(false);
  const [error,  setError]  = useState(null);

  const headers = { Authorization: `Bearer ${accessToken}` };

  const fetchPlan = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/niriksha/pricing`, { headers });
      if (res.ok) setPlan(await res.json());
    } catch { /* plan optional */ }
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/shulka/status/${passportId}`, { headers });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setStatus(data);
      if (!data.allowed) fetchPlan();
    } catch {
      setError(PUBLIC_ERROR);
      setStatus({ allowed: false, reason: "error" });
    }
  }, [passportId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  async function handlePayPassport() {
    setPaying(true);
    try {
      const orderRes = await fetch(`${API_BASE}/api/shulka/orders`, {
        method:  "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({ passport_id: passportId }),
      });
      if (!orderRes.ok) throw new Error("order creation failed");
      const { order_id, amount_paise, currency, key_id } = await orderRes.json();

      // Fallback: if Razorpay modal closes without firing onDismiss (e.g. browser
      // back button), reset paying when the window regains focus.
      function resetOnFocus() { setPaying(false); }

      openRazorpayCheckout({
        keyId:    key_id,
        orderId:  order_id,
        amount:   amount_paise,
        currency,
        onSuccess: async (response) => {
          window.removeEventListener("focus", resetOnFocus);
          await fetch(`${API_BASE}/api/shulka/verify`, {
            method:  "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body:    JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              passport_id:         passportId,
            }),
          });
          await fetchStatus();
          setPaying(false);
        },
        onDismiss: () => {
          window.removeEventListener("focus", resetOnFocus);
          setPaying(false);
        },
      });
      window.addEventListener("focus", resetOnFocus, { once: true });
    } catch {
      setError(PUBLIC_ERROR);
      setPaying(false);
    }
  }

  async function handleSubscribe() {
    setPaying(true);
    try {
      const orderRes = await fetch(`${API_BASE}/api/shulka/subscription/orders`, {
        method:  "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({ plan_type: "monthly" }),
      });
      if (!orderRes.ok) throw new Error("order creation failed");
      const { order_id, amount_paise, currency, key_id } = await orderRes.json();

      function resetOnFocus() { setPaying(false); }

      openRazorpayCheckout({
        keyId:    key_id,
        orderId:  order_id,
        amount:   amount_paise,
        currency,
        onSuccess: async (response) => {
          window.removeEventListener("focus", resetOnFocus);
          await fetch(`${API_BASE}/api/shulka/subscription/verify`, {
            method:  "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body:    JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              plan_type:           "monthly",
            }),
          });
          await fetchStatus();
          setPaying(false);
        },
        onDismiss: () => {
          window.removeEventListener("focus", resetOnFocus);
          setPaying(false);
        },
      });
      window.addEventListener("focus", resetOnFocus, { once: true });
    } catch {
      setError(PUBLIC_ERROR);
      setPaying(false);
    }
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (!status) {
    return <span aria-label="Payment status loading" className="tl-loading">Checking…</span>;
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error && !status.allowed) {
    return <p className="tl-error" role="alert">{error}</p>;
  }

  // ── Allowed → pass through ──────────────────────────────────────────────────
  if (status.allowed) return <>{children}</>;

  // ── Payment panel ───────────────────────────────────────────────────────────
  return (
    <div className="tl-payment-gate" aria-label="Payment panel">
      <p className="tl-payment-gate-heading">Unlock this PassPort</p>

      <div className="tl-payment-gate-options">

        <div className="tl-payment-gate-card">
          <p className="tl-payment-gate-card-label">This PassPort only</p>
          <p className="tl-payment-gate-card-price" aria-label="Per-passport price">
            {plan ? paise(plan.per_passport_price) : "—"}
          </p>
          <button
            className="tl-button-pay"
            aria-label="Pay for this PassPort"
            disabled={paying}
            onClick={handlePayPassport}
          >
            {paying ? "Processing…" : "Pay & Download"}
          </button>
        </div>

        <div className="tl-payment-gate-card">
          <p className="tl-payment-gate-card-label">Monthly subscription</p>
          <p className="tl-payment-gate-card-price" aria-label="Monthly subscription price">
            {plan ? paise(plan.subscription_monthly_price) : "—"}/month
          </p>
          <button
            className="tl-button-pay tl-button-pay--secondary"
            aria-label="Subscribe monthly"
            disabled={paying}
            onClick={handleSubscribe}
          >
            {paying ? "Processing…" : "Subscribe & Download All"}
          </button>
        </div>

      </div>

      {error && <p className="tl-error" role="alert" style={{ marginTop: "0.5rem" }}>{error}</p>}
    </div>
  );
}
